import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {
    useInventarFotkyQuery,
    useUploadInventarFotografieMutation,
    useDeleteInventarFotografieMutation,
    useInvalidovatInventarFotky,
} from '../../api/JsdhNotifikaceService/service/InventarMutations';
import InventarServiceApi from '../../api/JsdhNotifikaceService/service/InventarService';
import { zmensiObrazek } from '../../utils/zmensiObrazek';
import { useToast } from '../toast/useToast';

const MAX_FOTEK = 5;

/**
 * Musi sedet se serverem. Atribut `accept` na inputu je jen napoveda — uzivatel
 * si v dialogu prepne na "vsechny soubory" a vybere co chce.
 */
const POVOLENE_TYPY = ['image/png', 'image/jpeg'];

/** Nahled jedne fotky. Obrazek jde pres autentizovany endpoint, proto blob + objectURL. */
function Nahled({ polozkaId, fotoId, hlavni, onSmazat }: {
    polozkaId: number; fotoId: number; hlavni: boolean; onSmazat: () => void;
}) {
    const [url, setUrl] = useState<string | null>(null);

    useEffect(() => {
        let zivy = true;
        let vytvorene: string | null = null;
        InventarServiceApi.getFotkaBlob(polozkaId, fotoId)
            .then((blob) => {
                if (!zivy) return;
                vytvorene = URL.createObjectURL(blob);
                setUrl(vytvorene);
            })
            .catch(() => undefined);
        // Bez revoke by kazde otevreni zalozky nechalo blob viset v pameti az do reloadu.
        return () => {
            zivy = false;
            if (vytvorene) URL.revokeObjectURL(vytvorene);
        };
    }, [polozkaId, fotoId]);

    return (
        <Box sx={{ position: 'relative', width: 160 }}>
            {url ? (
                <Box component="img" src={url} alt="Fotka položky"
                     sx={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 1, display: 'block' }} />
            ) : (
                <Box sx={{ width: 160, height: 120, borderRadius: 1, bgcolor: 'action.hover' }} />
            )}
            {hlavni && (
                <Chip label="Hlavní" size="small" color="primary"
                      sx={{ position: 'absolute', top: 4, left: 4 }} />
            )}
            <IconButton size="small" onClick={onSmazat} aria-label="Smazat fotku"
                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper' }}>
                <DeleteIcon fontSize="small" />
            </IconButton>
        </Box>
    );
}

export default function InventarFotky({ polozkaId }: { polozkaId: number }) {
    const { data: fotky = [], isLoading, isError, refetch } = useInventarFotkyQuery(polozkaId);
    const nahrat = useUploadInventarFotografieMutation(polozkaId);
    const smazat = useDeleteInventarFotografieMutation(polozkaId);
    const prenactiFotky = useInvalidovatInventarFotky(polozkaId);
    const { showToast } = useToast();
    const [probiha, setProbiha] = useState(false);

    const vyber = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const soubory = Array.from(e.target.files ?? []);
        e.target.value = '';
        if (soubory.length === 0) return;

        // Typ se kontroluje na PUVODNIM souboru, PRED zmensenim. Bez toho by
        // prosel jakykoli dekodovatelny obrazek: `zmensiObrazek` vse prekresli
        // na canvas a vrati JPEG, takze by GIF i SVG dorazily na server jako
        // image/jpeg a serverova kontrola typu by je nikdy nevidela.
        const spatnyTyp = soubory.filter((f) => !POVOLENE_TYPY.includes(f.type));
        const podporovane = soubory.filter((f) => POVOLENE_TYPY.includes(f.type));

        const volno = MAX_FOTEK - fotky.length;
        const kNahrani = podporovane.slice(0, volno);
        const zahozeno = podporovane.length - kNahrani.length;

        if (kNahrani.length === 0) {
            showToast(
                spatnyTyp.length === soubory.length
                    ? 'Podporované jsou jen PNG a JPEG. Nenahráno nic.'
                    : `Položka už má maximum ${MAX_FOTEK} fotek. Nenahráno nic.`,
                'error',
            );
            return;
        }

        setProbiha(true);
        // Serialne: endpoint bere jeden soubor a u paralelniho odeslani by se hur
        // hlasilo, ktery konkretni soubor selhal.
        let uspech = 0;
        const chyby: string[] = [];
        for (const soubor of kNahrani) {
            try {
                const zmenseny = await zmensiObrazek(soubor);
                await nahrat.mutateAsync({ soubor: zmenseny, nazev: soubor.name });
                uspech += 1;
            } catch (err) {
                // Hlasku serveru NEzahazovat: rozlisuje "prilis velky soubor",
                // "uz mate pet fotek" a "nepodporovany typ" — bez ni uzivatel
                // vidi jen jmeno souboru a nevi, co s tim.
                const duvod = err instanceof Error && err.message ? `: ${err.message}` : '';
                chyby.push(`${soubor.name}${duvod}`);
            }
        }
        setProbiha(false);
        // Jednou za davku, ne po kazde fotce — pet fotek by jinak vyvolalo pet
        // refetchu seznamu misto jednoho.
        prenactiFotky();

        if (chyby.length === 0 && zahozeno === 0 && spatnyTyp.length === 0) {
            showToast(`Nahráno ${uspech} z ${kNahrani.length}.`, 'success');
        } else {
            const casti = [`Nahráno ${uspech} z ${soubory.length}.`];
            if (spatnyTyp.length) {
                casti.push(`Podporované jsou jen PNG a JPEG, přeskočeno: ${spatnyTyp.map((f) => f.name).join(', ')}.`);
            }
            if (chyby.length) casti.push(`Nepodařilo se: ${chyby.join(', ')}.`);
            if (zahozeno) casti.push(`${zahozeno} nad limit ${MAX_FOTEK} fotek.`);
            showToast(casti.join(' '), 'error');
        }
    };

    if (isLoading) {
        return <Paper sx={{ p: 2 }}><CircularProgress size={24} /></Paper>;
    }

    // Bez tehle vetve vypada selhani nacteni jako "polozka nema fotky" — a admin
    // by v dobre vire nahral kopie fotek, ktere uz tam jsou.
    if (isError) {
        return (
            <Paper sx={{ p: 2 }}>
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    Fotky se nepodařilo načíst.
                </Typography>
                <Button variant="outlined" onClick={() => refetch()}>Zkusit znovu</Button>
            </Paper>
        );
    }

    const plno = fotky.length >= MAX_FOTEK;

    return (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Button component="label" variant="contained" startIcon={<PhotoCameraIcon />}
                        disabled={plno || probiha}>
                    {probiha ? 'Nahrávám...' : 'Nahrát fotky'}
                    <input hidden type="file" multiple accept="image/png,image/jpeg" onChange={vyber} />
                </Button>
                <Typography variant="body2" color="text.secondary">
                    {plno
                        ? `Položka má maximum ${MAX_FOTEK} fotek. Chceš-li přidat další, nejdřív některou smaž.`
                        : `${fotky.length} z ${MAX_FOTEK} fotek`}
                </Typography>
            </Box>

            {fotky.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    U této položky zatím nejsou žádné fotky.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {fotky.map((f, i) => (
                        <Nahled key={f.id} polozkaId={polozkaId} fotoId={f.id} hlavni={i === 0}
                                onSmazat={() => {
                                    if (window.confirm('Opravdu smazat tuto fotku? Smazání je nevratné.')) {
                                        smazat.mutate(f.id);
                                    }
                                }} />
                    ))}
                </Box>
            )}
        </Paper>
    );
}
